import React from "react";
import moment from "moment";
import {
	formatBsDateString,
	fromAdToBs,
	rupeeConverter,
} from "../../global/function";
import { renderBillTags } from "./bill-list-item.component";
import type { DataTableColumn } from "../data-table/data-table.component";
import type { Bill } from "../../queries/bill.queries";

// Column set for BillList's table view. Takes its "which financial fields
// matter" cue from the deleted materialized-book view (git show
// 90fa5e6^:src/components/pdf-template/sales-book.component.tsx) but trims it
// to what's worth a persistent column on an interactive, filterable list
// rather than a print ledger: drops Non-Taxable/Export Sales (usually zero,
// same reasoning BillSummaryCard already applies to those fields) and the
// running-totals footer row (ill-defined on an infinite-scroll list — the
// real ledger is still the BE-generated PDF, not this view).
export const billTableColumns: DataTableColumn<Bill>[] = [
	{
		key: "invoice_number",
		label: "Invoice #",
		render: (bill) => bill.invoice_number,
	},
	{
		key: "invoice_date",
		label: "Invoice Date",
		align: "right",
		render: (bill) => (
			<div className="bill-table-date">
				<div className="bill-table-date-en">
					{moment(bill.invoice_date).format("DD MMMM, YYYY")}
				</div>
				<div className="bill-table-date-np">
					{bill.invoice_date_nepali
						? formatBsDateString(bill.invoice_date_nepali)
						: fromAdToBs(bill.invoice_date, "DD MMMM, YYYY")}
				</div>
			</div>
		),
	},
	{
		key: "sales_date",
		label: "Sales Date",
		align: "right",
		render: (bill) => (
			<div className="bill-table-date">
				<div className="bill-table-date-en">
					{moment(bill.sales_date).format("DD MMMM, YYYY")}
				</div>
				<div className="bill-table-date-np">
					{fromAdToBs(bill.sales_date, "DD MMMM, YYYY")}
				</div>
			</div>
		),
	},
	{
		key: "buyer_name",
		label: "Buyer",
		render: (bill) => (
			<div className="bill-table-buyer">
				<div className="cap-display">{bill.buyer_name}</div>
				{bill.buyer_pan && (
					<div className="bill-table-pan">PAN: {bill.buyer_pan}</div>
				)}
			</div>
		),
	},
	{
		key: "status",
		label: "Status",
		render: (bill) => (
			<div>
				{renderBillTags(bill)}
				{bill.ird_bill === "success" && bill.ird_synced_at && (
					<div className="bill-table-ird-synced">
						IRD synced · {moment(bill.ird_synced_at).fromNow()}
					</div>
				)}
			</div>
		),
	},
	{
		key: "sub_total",
		label: "Sub Total",
		align: "right",
		render: (bill) => `Rs. ${rupeeConverter(bill.sub_total)}`,
	},
	{
		key: "discount",
		label: "Discount",
		align: "right",
		render: (bill) => {
			const discount =
				(bill.taxable_discount ?? 0) + (bill.non_taxable_discount ?? 0);
			return discount > 0 ? `Rs. ${rupeeConverter(discount)}` : "—";
		},
	},
	{
		key: "tax",
		label: "VAT/ESF",
		align: "right",
		render: (bill) => {
			if (bill.esf) return `Rs. ${rupeeConverter(bill.esf)} (ESF)`;
			if (bill.vat) return `Rs. ${rupeeConverter(bill.vat)} (VAT)`;
			return "—";
		},
	},
	{
		key: "total_sales",
		label: "Total",
		align: "right",
		className: "bill-table-total",
		render: (bill) => `Rs. ${rupeeConverter(bill.total_sales)}`,
	},
];
